/**
 * What number will the shot Unity is about to insert after `shot` end up with?
 *
 * The AI Workspace's Create tab announces the shot it is filling BEFORE it exists ("Creating Shot
 * 1C…"), because the insert only happens on Apply. Unity owns numbering, so this is a PREDICTION —
 * the authority is `DatabaseManager.GetNextAvailableShotNumberVariantForShotNumber` +
 * `NumberingUtilities.GetNextShotVariant` (locked) and the position-derived recompute
 * `applyComputedNumbering` runs on the repush (unlocked). THREE rules, and getting the branch wrong
 * makes the label lie in whichever mode you guessed at:
 *
 *  - UNLOCKED, auto-numbered source: `insertShot` duplicates the row in place and everything after
 *    it shifts down, so the new shot takes the source's successor position — B → C, and the old C
 *    becomes D. Derived from the position in `scene.shots` (already in numbering order, hidden and
 *    `Continuing` shots excluded by previsLoader), never from `shot.order`, which can have gaps —
 *    and counting only the AUTO rows, because that is what the recompute's counter counts.
 *  - LOCKED: Unity assigns the next VARIANT of the source's own number rather than the next letter
 *    — B → B-1, 1A → 2A, A1 → A2 (and a digit-only number is left ALONE; see nextShotVariant).
 *  - UNLOCKED with a MANUAL source number: no variant and no position — `DuplicateShotInScene`
 *    copies the `number` column verbatim (DatabaseManager.cs:13985) and the unlocked recompute
 *    hands a manual row straight back, so the new shot wears the SAME label as its source until
 *    someone edits one. Predicted verbatim: a duplicate is the truth here, a variant would be a
 *    comfortable lie.
 *
 * Every arm is best-effort by construction: locked numbering retries the variant until it is
 * unique, so a collision resolves to something else on the way back. The label is a heading, never
 * a key.
 */

import { shotIndexToLabel } from './numberingFormatter.ts';
import type { DocumentSettings, TimelineScene, TimelineShot } from '@/types/timeline.ts';

/**
 * Port of the simple arm of Unity's `NumberingUtilities.GetNextShotVariant`: decompose the number
 * into letters / punctuation / digits, then increment the numeric part wherever it sits. With no
 * digits at all the variant counter starts at 1 and brings a "-" with it.
 *
 * Vectors (NumberingUtilities.cs test set): "ABC" → "ABC-1", "1A" → "2A", "A1" → "A2", "B" → "B-1".
 * Unity's full decomposer also has Unknown/Illegal arms that return the input untouched; anything
 * this pair of patterns cannot parse (empty, "A1B", "1-2-3") takes that same no-op path.
 *
 * A DIGIT-ONLY number takes it too, and that one is counter-intuitive enough to spell out:
 * `DecomposeShotNumber` classifies a single numeric token as `Illegal` ("the shot number cannot be
 * numeric normally", NumberingUtilities.cs:391-398) and `GetNextShotVariant` returns the input
 * unchanged for Illegal exactly as it does for Unknown (:123-126). So locked "3" stays "3" — and
 * the uniqueness loop around it does not rescue the label either: it breaks out the moment a
 * variant stops changing (DatabaseManager.cs:17212-17216), leaving a real duplicate. Unity's
 * behaviour is the contract; predicting "4" would make the heading lie in digits mode.
 */
export function nextShotVariant(shotNumber: string): string {
  const s = (shotNumber ?? '').trim();
  if (!s) return shotNumber;

  // Illegal (single numeric token) — no-op, above.
  if (/^\d+$/.test(s)) return shotNumber;

  // LetterNumber — letters, optional punctuation, optional digits ("B", "B-1", "A1", "ABC").
  const letterNumber = /^([A-Za-z]+)([^A-Za-z0-9]*)(\d*)$/.exec(s);
  if (letterNumber) {
    const [, alpha, punct, digits] = letterNumber;
    if (!digits) return `${alpha}-1`;
    return `${alpha}${punct}${Number(digits) + 1}`;
  }

  // NumberLetter — digits, optional punctuation, optional letters ("1A", "3", "1-A").
  const numberLetter = /^(\d+)([^A-Za-z0-9]*)([A-Za-z]*)$/.exec(s);
  if (numberLetter) {
    const [, digits, punct, alpha] = numberLetter;
    return `${Number(digits) + 1}${punct}${alpha}`;
  }

  return shotNumber;
}

/**
 * True when this shot's number was typed by a user rather than derived from its position.
 *
 * `numberOverride` is the raw `shots.number` column; `number` is the display value the recompute
 * writes back (`applyComputedNumbering`, store/actions.ts:140-167), which is why the override — not
 * the display — is the thing to test, and the thing Unity copies on a duplicate.
 */
function hasManualNumber(shot: Pick<TimelineShot, 'numberOverride'>): boolean {
  return !!(shot.numberOverride && shot.numberOverride.trim() !== '');
}

/**
 * The shot number an `insertShot` on `shot` is expected to produce. Returns the SHOT half only —
 * compose the displayed "1C" with `composeShotNumberLabel(scene.number, separator, …)`.
 */
export function predictInsertedShotNumber(
  scene: TimelineScene | null | undefined,
  shot: TimelineShot,
  documentSettings?: DocumentSettings,
): string {
  const locked = documentSettings?.isNumberingLocked ?? false;
  if (locked) return nextShotVariant(shot.number);

  // Unlocked + a manual source: the copied `number` column survives the recompute untouched, so the
  // new shot's label IS the source's. Reported as the duplicate it is (see the header note).
  if (hasManualNumber(shot)) return shot.numberOverride!.trim();

  const index = scene?.shots.findIndex(s => s.id === shot.id) ?? -1;
  // No scene (or the shot is not in it) — fall back to the variant rule rather than guessing a
  // position we cannot see.
  if (index < 0) return nextShotVariant(shot.number);

  // The counter the recompute uses is NOT the array index: a manually-numbered row takes its own
  // number and leaves the counter where it was (`computeShotNumbers`, numberingFormatter.ts:65-77).
  // So the inserted shot's counter value is the number of AUTO rows at or before the anchor — with
  // any manual shot earlier in the scene, `index + 1` skips a letter and the heading lies.
  const autoShotsThroughAnchor = (scene?.shots ?? [])
    .slice(0, index + 1)
    .filter(s => !hasManualNumber(s))
    .length;

  return shotIndexToLabel(
    autoShotsThroughAnchor,
    documentSettings?.useFullAlphabet ?? false,
    documentSettings?.useDigitsForShots ?? false,
  );
}
